import { z } from 'zod';
import { isoDateSchema, mobileSchema, objectIdSchema, pageSchema } from './common.js';
import { goalKeySchema } from './user.js';
/**
 * The marketing site's waitlist — people who want the app before it ships.
 *
 * Deliberately NOT a user account: CLAUDE.md §2.8 says no account exists until an OTP
 * succeeds, and signing up here verifies nothing. A waitlist entry is a lead; it never
 * becomes a `users` row by itself.
 */
/** What someone came for: one of our goals, or something we have not thought of. */
export const OTHER_PURPOSE = 'OTHER';
export const waitlistPurposeSchema = z.union([goalKeySchema, z.literal(OTHER_PURPOSE)]);
/** Same ceiling as onboarding's goals, plus room for OTHER alongside three real ones. */
export const WAITLIST_MAX_PURPOSES = 4;
export const createWaitlistBodySchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    email: z.string().trim().toLowerCase().email('Enter a valid email address').max(160),
    mobile: mobileSchema,
    purposes: z.array(waitlistPurposeSchema).min(1).max(WAITLIST_MAX_PURPOSES),
    /** Free text, only meaningful when OTHER is among the purposes. */
    otherInterest: z.string().trim().max(120).optional(),
    /** Which page or campaign sent them, for reading the funnel later. */
    source: z.string().trim().max(60).optional(),
    /**
     * Honeypot. Real people never see this field, so anything in it is a bot. Named
     * plausibly on purpose — `company` is what autofill scripts reach for.
     */
    company: z.string().max(200).optional(),
})
    .refine((v) => !v.purposes.includes(OTHER_PURPOSE) || (v.otherInterest?.length ?? 0) > 0, {
    message: 'Tell us what you are looking for',
    path: ['otherInterest'],
});
/**
 * The public response says nothing back about the entry — not even whether the email was
 * already on the list. A waitlist that answers "you are already signed up" is an email
 * oracle for anyone who wants to test addresses against it.
 */
export const createWaitlistResponseSchema = z.object({ ok: z.literal(true) });
/** The portal's view. Everything a person typed, plus when. */
export const waitlistEntryResponseSchema = z.object({
    id: objectIdSchema,
    name: z.string(),
    email: z.string(),
    mobile: z.string(),
    purposes: z.array(waitlistPurposeSchema),
    otherInterest: z.string().nullable(),
    source: z.string().nullable(),
    createdAt: isoDateSchema,
    /** Later than createdAt when someone signed up again with new answers. */
    updatedAt: isoDateSchema,
});
export const listWaitlistQuerySchema = z.object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    /** Matches name, email or mobile. */
    search: z.string().trim().max(80).optional(),
    purpose: waitlistPurposeSchema.optional(),
});
export const waitlistPageSchema = pageSchema(waitlistEntryResponseSchema).extend({
    /** Total entries, so the portal can show a number without walking every page. */
    total: z.number().int(),
});
//# sourceMappingURL=waitlist.js.map